import { motion } from "framer-motion";

const STAGES = [
  {
    n: "01",
    title: "Enquiry & Estimate",
    body: "Send your plans and dimensions. Once details are confirmed we email a formal estimate valid for 14 days.",
  },
  {
    n: "02",
    title: "Order & Design",
    body: "A deposit locks in your order. We confirm every design, spec and hardware selection with you.",
  },
  {
    n: "03",
    title: "CAD & Sign-Off",
    body: "Draft shop drawings are issued, amendments folded in, and finalised drawings signed off digitally.",
  },
  {
    n: "04",
    title: "Production",
    body: "Signed drawings start the clock. Fabrication begins and we confirm the official lead time.",
  },
  {
    n: "05",
    title: "Completion & Shipping",
    body: "Completion images from the factory, final balance settled, then scheduled onto the next available shipment.",
  },
  {
    n: "06",
    title: "Delivery to Site",
    body: "Cleared through New Zealand customs and delivered safely to the front of your site by our freight partner.",
  },
];

export const ProcessSection = () => {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative z-10 overflow-hidden bg-[#12100d] px-6 py-28 lg:px-16 lg:py-40"
    >
      {/* Grain / bronze wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 1100px 600px at 50% -10%, rgba(198,168,124,0.18), transparent 65%)",
        }}
      />

      <div className="relative mx-auto max-w-[1440px]">
        {/* Big centered PROCESS. wordmark */}
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="font-serif-display mb-20 text-center text-6xl font-bold tracking-[-0.02em] text-white lg:mb-28 lg:text-8xl"
          data-testid="process-wordmark"
        >
          Process.
        </motion.h2>

        {/* Intro */}
        <div className="mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.19, 1, 0.22, 1] }}
            className="space-y-5 text-center font-body text-[15px] leading-relaxed text-white/75 lg:text-[17px]"
          >
            <p>
              At Eurosteel NZ, we pride ourselves on building strong,
              long-term relationships with our clients. Our team guides you
              through every step of the process, from concept to completion,
              making your experience simple, informed and enjoyable.
            </p>
            <p>
              Whether it&rsquo;s through a phone call, email or consultation,
              we take the time to understand your project, your needs and
              your vision — tailoring a custom solution that fits your
              architectural goals and budget.
            </p>
          </motion.div>
        </div>

        {/* 6-stage journey */}
        <div className="mt-20 lg:mt-28">
          <ol
            data-testid="process-steps"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6"
          >
            {STAGES.map((s, i) => (
              <motion.li
                key={s.n}
                data-testid={`process-stage-${i + 1}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.85,
                  delay: 0.08 + (i % 3) * 0.08,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className="group relative flex min-h-[300px] flex-col justify-between overflow-hidden rounded-2xl border border-white/10 bg-white p-8 shadow-[0_28px_60px_-24px_rgba(0,0,0,0.7)] transition-all duration-500 hover:-translate-y-1 hover:border-[#c6a87c] hover:shadow-[0_32px_70px_-20px_rgba(198,168,124,0.5)] lg:min-h-[340px] lg:p-10"
              >
                {/* Ambient bronze corner */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#c6a87c]/10 blur-3xl transition-opacity duration-700 group-hover:bg-[#c6a87c]/25"
                />

                {/* Top row — huge number */}
                <div className="relative flex items-start justify-between gap-4">
                  <span className="font-serif-display block text-[80px] font-bold leading-[0.9] tracking-[-0.05em] text-[#c6a87c] lg:text-[112px]">
                    {s.n}
                  </span>
                </div>

                {/* Title + body */}
                <div className="relative mt-4">
                  <div className="mb-5 h-[3px] w-14 bg-[#c6a87c] transition-[width] duration-500 group-hover:w-24" />
                  <h4 className="font-serif-display text-[26px] font-bold leading-[1.05] tracking-[-0.01em] text-black lg:text-[30px]">
                    {s.title}
                  </h4>
                  <p className="mt-3 font-body text-[13.5px] leading-[1.6] text-black/65 lg:text-[14px]">
                    {s.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
